import type { HighwayMaintenanceFeePenaltyTicket } from '#/components/ui/tickets/highway-maintenance-fee-penalty/model';
import { Group, Skeleton, Stack, Text, Tooltip } from '@mantine/core';
import { HighwayMaintenanceFeePenaltyStateBadge } from '../HighwayMaintenanceFeePenaltyStateBadge';
import { useHighwayMaintenanceFeePenaltyTicket } from '#/components/ui/tickets/highway-maintenance-fee-penalty/endpoints/ticketsHooks';
import {
  TICKET_TYPE,
  TICKET_TYPE_CONFIG,
} from '#/components/ui/tickets/config';
import { IconExternalLink } from '@tabler/icons-react';
import { getChainStorePayPlatformUrl } from '#/components/ui/tickets/utils';

const CONFIG =
  TICKET_TYPE_CONFIG[TICKET_TYPE.HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKETS];

type SummaryProps = {
  email: HighwayMaintenanceFeePenaltyTicket['account']['email'];
} & Pick<
  HighwayMaintenanceFeePenaltyTicket,
  'id' | 'idNumber' | 'plateNumber' | 'state' | 'taxIdNumber'
> & { paymentId: string | null | undefined };

const Summary = (props: SummaryProps) => {
  const ownerId = props.idNumber ?? props.taxIdNumber ?? '-';
  const chainStorePayPlatformUrl = getChainStorePayPlatformUrl(props.paymentId);
  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="xs">
          <Text fw={600} size="xl">
            {CONFIG.queryPlatform}
          </Text>
          <HighwayMaintenanceFeePenaltyStateBadge state={props.state} />
        </Group>
        {chainStorePayPlatformUrl && (
          <Tooltip label={props.paymentId} position="bottom">
            <a
              href={chainStorePayPlatformUrl}
              target="_blank"
              className="text-blue-500 flex min-w-0 shrink items-center gap-1"
            >
              <IconExternalLink size={16} className="shrink-0" />
              <span className="truncate">{props.paymentId}</span>
            </a>
          </Tooltip>
        )}
      </Group>
      <Group gap="md">
        <Text size="md" fw={500}>
          {`${props.email}．${props.plateNumber}．${ownerId}`}
        </Text>
      </Group>
    </Stack>
  );
};

export const HighwayMaintenanceFeePenaltySummary = ({
  id,
  paymentId,
}: {
  id: HighwayMaintenanceFeePenaltyTicket['id'] | undefined;
  paymentId: string | null | undefined;
}) => {
  const { data, isLoading } = useHighwayMaintenanceFeePenaltyTicket(id);

  if (!id) return null;

  if (isLoading) {
    return (
      <Stack gap="xs">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="xs">
            <Text fw={600} size="xl">
              {CONFIG.queryPlatform}
            </Text>
          </Group>
        </Group>
        <Stack gap="xs">
          <Group justify="space-between" wrap="nowrap">
            <Group gap="xs">
              <Skeleton height={24} width={120} />
              <Skeleton height={24} width={60} radius="xl" />
            </Group>
            <Skeleton height={16} width={80} />
          </Group>
          <Skeleton height={20} width="70%" />
        </Stack>
      </Stack>
    );
  }

  return (
    <>
      {data && (
        <Summary
          email={data.account.email}
          id={data.id}
          idNumber={data.idNumber}
          plateNumber={data.plateNumber}
          state={data.state}
          taxIdNumber={data.taxIdNumber}
          paymentId={paymentId}
        />
      )}
    </>
  );
};
